import { lowestPlanPrice, highestPlanPrice } from "@/data/pricing";
import { LastUpdated } from "@/components/site/LastUpdated";
import { applyPageMetadata } from "@/lib/page-metadata";
import { createFileRoute, notFound, redirect, Link } from "@tanstack/react-router";
import { Layout, Section, Eyebrow, CTAButton, Breadcrumbs, BOOKING_URL } from "@/components/site/Layout";
import { HubSpotForm } from "@/components/site/HubSpotForm";
import { LocationInlineText } from "@/components/site/LocationInlineText";
import { locationContent } from "@/data/location-content";
import { getLocation, type Location } from "@/data/locations";
import { locationSeoPages } from "@/data/location-seo-pages";
import { practiceAreas } from "@/data/practice-areas";
import { PricingTeaser } from "@/components/site/Pricing";
import { FAQSection } from "@/components/site/FAQSection";
import { TrustSignals } from "@/components/site/TrustSignals";
import { breadcrumbSchema, jsonLdScript, ogImageFor } from "@/lib/seo";

// Short forms accepted in place of the full place name inside FAQ questions.
const PLACE_SHORT_FORMS: Record<string, string> = {
  "Washington DC": "DC",
};

// Ensure every FAQ question carries the page's place name without editing the content files.
function faqWithPlace(faqs: { q: string; a: string }[], place: string) {
  const short = PLACE_SHORT_FORMS[place];
  return faqs.map((f) => {
    const haystack = f.q.toLowerCase();
    if (haystack.includes(place.toLowerCase()) || (short && haystack.includes(short.toLowerCase()))) return f;
    const body = f.q.trim().replace(/\?+$/, "");
    return { ...f, q: `${body} in ${place}?` };
  });
}

export const Route = createFileRoute("/lawyer-seo/$slug")({
  loader: ({ params }) => {
    const raw = params.slug;
    // Legacy URLs (no -seo suffix) 301 to the keyword-rich URL.
    if (!raw.endsWith("-seo")) {
      const suffixed = `${raw}-seo`;
      if (getLocation(suffixed)) {
        throw redirect({ to: "/lawyer-seo/$slug", params: { slug: suffixed }, statusCode: 301 });
      }
    }
    const loc = getLocation(raw);
    if (!loc) throw notFound();
    return { loc };
  },
  head: ({ params, loaderData }) => {
    const l = loaderData?.loc;
    if (!l) return { meta: [{ title: "Location" }] };
    const url = `/lawyer-seo/${params.slug}`;
    const absUrl = `https://pageonelawyers.com${url}`;
    const page = locationSeoPages[params.slug];
    if (!page) return { meta: [{ title: "Location — PageOne Lawyers" }] };
    const ogImage = ogImageFor("location", params.slug);
    return applyPageMetadata(`/lawyer-seo/${params.slug}`, {
      meta: [
        { title: `Lawyer SEO ${l.location} | Law Firm SEO Company` },
        { name: "description", content: `Lawyer SEO in ${l.location}: rank in the local pack, win higher-value cases and outgrow competing ${l.location} law firms.` },
        { name: "robots", content: "index,follow" },
        { property: "og:title", content: `Lawyer SEO ${l.location} | Law Firm SEO Company` },
        { property: "og:description", content: `Lawyer SEO in ${l.location}: rank in the local pack, win higher-value cases and outgrow competing ${l.location} law firms.` },
        { property: "og:url", content: absUrl },
        { property: "og:type", content: "article" },
        { property: "og:image", content: ogImage },
        { property: "og:image:type", content: "image/svg+xml" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: `Lawyer SEO in ${l.location} — PageOne Lawyers` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `Lawyer SEO ${l.location} | Law Firm SEO Company` },
        { name: "twitter:description", content: `Lawyer SEO in ${l.location}: rank in the local pack, win higher-value cases and outgrow competing ${l.location} law firms.` },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: absUrl }],
      scripts: [
        jsonLdScript({
          "@context": "https://schema.org",
          "@type": locationContent[params.slug]?.exactSource ? "LegalService" : "Service",
          name: page.h1,
          serviceType: "Search engine optimization for law firms",
          description: page.description,
          url: absUrl,
          provider: { "@type": "Organization", name: "PageOne Lawyers", url: "https://pageonelawyers.com/" },
          areaServed: { "@type": page.areaType, name: page.place },
          offers: { "@type": "AggregateOffer", priceCurrency: "USD", lowPrice: lowestPlanPrice, highPrice: highestPlanPrice, offerCount: "6", url: "https://pageonelawyers.com/pricing" },
        }),
        jsonLdScript(breadcrumbSchema([
          { label: "Home", path: "/" },
          { label: "Locations", path: "/lawyer-seo" },
          ...(page.breadcrumbParent ? [{ label: page.breadcrumbParent.name, path: page.breadcrumbParent.url }] : []),
          { label: page.place, path: url },
        ])),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: (locationContent[params.slug]?.exactSource ? locationContent[params.slug].faqs : faqWithPlace(locationContent[params.slug]?.faqs ?? [], page.place)).map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
          }),
        },
      ],
    });
  },
  component: LocationPage,
  notFoundComponent: () => (
    <Layout><Section><h1 className="text-3xl font-semibold text-foreground">Location not found</h1></Section></Layout>
  ),
  errorComponent: () => (
    <Layout><Section><h1 className="text-3xl text-foreground">Something went wrong</h1></Section></Layout>
  ),
});

function LocationPage() {
  const { loc: l } = Route.useLoaderData() as { loc: Location };
  const page = locationSeoPages[l.slug];
  const content = locationContent[l.slug];
  if (!page || !content) return null;
  const faqs = content.exactSource ? content.faqs : faqWithPlace(content.faqs, page.place);
  const orderedPractices = content.practiceOrder.flatMap((slug) => {
    const practice = practiceAreas.find((item) => item.slug === slug);
    return practice ? [practice] : [];
  });

  return (
    <Layout>
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Locations", href: "/lawyer-seo" }, ...(page.breadcrumbParent ? [{ label: page.breadcrumbParent.name, href: page.breadcrumbParent.url }] : []), { label: page.place }]} />
          <Eyebrow><span className="text-[var(--gold)]">Location</span></Eyebrow>
          <h1 className="mt-6 font-display text-5xl md:text-6xl font-semibold leading-tight max-w-3xl text-primary-foreground">{page.h1}</h1><LastUpdated />
          <p className="mt-6 text-xl text-primary-foreground/85 max-w-3xl">{content.hero}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <CTAButton to="#free-audit" variant="gold">Get a free {l.location} SEO audit</CTAButton>
            <CTAButton to={BOOKING_URL} variant="ghostLight">Talk to a strategist</CTAButton>
          </div>
        </div>
      </section>

      <Section>
        <div className="grid md:grid-cols-3 gap-12">
          <div className="md:col-span-2 space-y-6">
            <h2 className="text-3xl font-semibold text-foreground">{content.whyHeading}</h2>
            {content.why.map((text) => <p key={text} className="text-lg text-foreground leading-relaxed"><LocationInlineText text={text} /></p>)}
            <h3 className="text-2xl font-semibold text-foreground mt-10">{content.includedHeading}</h3>
            <ul className="space-y-3">
              {content.checklist.map((d) => (
                <li key={d} className="flex gap-3"><span className="text-[var(--gold-deep)] font-bold">✓</span><span className="text-foreground">{d}</span></li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-2">
            <h2 className="text-3xl font-semibold text-foreground">{content.marketHeading}</h2>
            <div className="mt-6 max-w-4xl space-y-6">{content.market.map((text) => <p key={text} className="text-lg text-foreground leading-relaxed"><LocationInlineText text={text} /></p>)}</div>
            {content.marketLinks.length > 0 && <p className="mt-6 text-foreground leading-relaxed">
              {page.areaType === "State" ? `Explore the existing city programs within ${page.place}: ` : `For the wider office footprint beyond ${page.place}, visit `}
              {content.marketLinks.map((item, index) => <span key={item.url}>{index > 0 && ", "}<Link to="/lawyer-seo/$slug" params={{ slug: item.url.slice("/lawyer-seo/".length) }} className="text-primary underline underline-offset-4">{page.areaType === "State" ? `law firm SEO in ${item.name}` : `our ${item.name} law firm SEO page`}</Link></span>)}.
            </p>}
          </div>
          <aside className="rounded-xl border border-border bg-secondary/50 p-6 h-fit md:col-start-3 md:row-start-1 md:row-span-2 md:sticky md:top-24">
            <h2 className="text-xs uppercase tracking-wider font-semibold text-[var(--gold-deep)]">{content.keywordHeading}</h2>
            <p className="mt-2 text-xs text-muted-foreground">{content.keywordIntro}</p>
            <ul className="mt-4 space-y-2 text-sm">
              {content.keywords.map((k) => (
                <li key={k} className="flex items-start gap-2 text-foreground"><span className="text-[var(--gold-deep)]">›</span><span>{k}</span></li>
              ))}
            </ul>
          </aside>
        </div>
      </Section>

      <div className="bg-secondary/40 border-y border-border">
        <Section>
          <Eyebrow>Practice areas</Eyebrow>
          <h2 className="mt-4 text-4xl font-semibold text-foreground max-w-3xl">{content.practiceHeading}</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {orderedPractices.map((p) => (
              <Link key={p.slug} to="/practice-areas/$slug" params={{ slug: p.slug }} className="group rounded-lg border border-border bg-background p-5 hover:border-[var(--gold)] transition">
                <div className="text-base font-semibold text-foreground">{p.shortName} SEO</div>
                <div className="mt-3 text-sm font-semibold text-primary group-hover:text-[var(--gold-deep)]">View strategy →</div>
              </Link>
            ))}
          </div>
          {content.focus.map((focus) => <div key={focus.heading} className="mt-10 max-w-4xl">
            <h3 className="text-2xl font-semibold text-foreground">{focus.heading}</h3>
            <p className="mt-4 text-lg text-foreground leading-relaxed">{content.exactSource ? <LocationInlineText text={focus.text} /> : <>{focus.text} <a href={focus.url} className="text-primary underline underline-offset-4">{focus.label}</a>{focus.additionalLinks?.map((item) => <span key={item.url}> · <a href={item.url} className="text-primary underline underline-offset-4">{item.label}</a></span>)}.</>}</p>
          </div>)}
        </Section>
      </div>

      <PricingTeaser title={`${l.location} lawyer SEO plans from $200/month.`} sub={`Choose the plan that fits your ${l.location} firm, from single-location local campaigns to enterprise multi-market programs. Transparent reporting on rankings, calls and signed cases in every tier.`} />

      <div className="bg-secondary/40 border-y border-border">
        <Section>
          {page.caseStudy ? <>
            <h2 className="mt-4 text-4xl font-semibold text-foreground max-w-3xl">{page.caseStudy.title}</h2>
            <p className="mt-6 text-lg text-foreground leading-relaxed max-w-3xl">{page.caseStudy.text}</p>
            <div className="mt-6"><CTAButton to={page.caseStudy.url} variant="ghost">Read the full case study →</CTAButton></div>
          </> : <>
            <p className="text-lg text-foreground leading-relaxed">{content.caseDirectoryText}</p>
            <div className="mt-6"><CTAButton to="/case-studies" variant="ghost">View case studies →</CTAButton></div>
          </>}
        </Section>
      </div>

      <TrustSignals
        intro={content.trustIntro}
        processNote={content.processNote}
      />

      <FAQSection
        tone="muted"
        title={`Lawyer & Law Firm SEO in ${page.place}: Questions Firms Ask`}
        items={faqs}
        semanticQuestionHeadings
      />

      <Section>
        <h2 className="text-2xl font-semibold text-foreground mb-6">{content.nearbyHeading}</h2>
        <div className="flex flex-wrap gap-2">
          {content.nearbyLinks.map((o) => (
            <Link key={o.url} to="/lawyer-seo/$slug" params={{ slug: o.url.slice("/lawyer-seo/".length) }} className="rounded-full border border-border px-4 py-2 text-sm text-foreground hover:border-[var(--gold)] hover:text-[var(--gold-deep)] transition">{o.name}</Link>
          ))}
        </div>
        <p className="mt-6 text-foreground leading-relaxed">{content.nearIntro ? <LocationInlineText text={content.nearIntro} /> : <>{content.servicesText} <Link to="/services/law-firm-seo" className="text-primary hover:text-gold-deep underline underline-offset-4">law firm SEO services</Link>.</>}</p>
        {content.bostonAi && <p className="mt-4"><Link to="/services/ai-seo-geo" className="text-primary underline underline-offset-4">AI SEO for law firms in Boston</Link></p>}
        <Link to="/lawyer-seo" className="mt-4 inline-block text-sm font-semibold text-primary hover:text-gold-deep">All locations →</Link>
      </Section>

      <section id="free-audit" className="bg-hero-gradient text-primary-foreground scroll-mt-24">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-14 sm:px-6 sm:py-20 lg:grid-cols-2 lg:items-start">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold mb-5">{content.finalHeading ?? `Ready to dominate search in ${l.location}?`}</h2>
            <p className="text-primary-foreground/80 text-lg mb-8">{content.finalText}</p>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <CTAButton to="#free-audit" variant="gold">Get my free SEO audit</CTAButton>
              <CTAButton to={BOOKING_URL} variant="ghostLight">Book a strategy call</CTAButton>
            </div>
          </div>
          <div className="min-w-0 rounded-lg bg-background p-5 text-foreground sm:p-8">
            <p className="mb-5 text-lg">{content.formLine ?? `Tell us about your ${page.place} firm and we'll send a prioritized SEO audit shortly.`}</p>
            <HubSpotForm lazy locationAudit />
          </div>
        </div>
      </section>
    </Layout>
  );
}